import { LDFlagValue } from 'launchdarkly-js-sdk-common'
import config from 'src/config/app.json';
import { UserRole } from '@alucio/aws-beacon-amplify/src/models';

export enum FlagCategory {
  FEATURE = 'feature',
  USER_PERMISSION = 'userPermission',
}

export interface FeatureFlag {
  flag: string,
  defaultValue: LDFlagValue,
  ALLOWED_ROLES?: readonly UserRole[]
  CATEGORY?: readonly FlagCategory[],
}

// [NOTE] - Please add the `as` assertion to `defaultValue` so we can cast
//          the type correctly

const STANDARD_ROLES = [
  UserRole.TENANT_PUBLISHER,
  UserRole.TENANT_VIEWER,
];

export const FeatureFlags = {
  test: {
    flag: 'fakeFlag',
    defaultValue: 1 as number,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableDocumentSummaries: {
    flag: 'enable_document_summaries',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableMantleSummaryDebug: {
    flag: 'enable_mantle_summary_debug',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableEmailTemplates: {
    flag: 'BEAC_2516_Enable_Document_Sharing_Templates',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableCustomDeckSharedFolders: {
    // [TODO:] 2718 remove this when we have a new feature flag for collaboration
    flag: 'BEAC_2712_custom_decks_shared_folders',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableAddNewMeeting: {
    flag: 'BEAC_2829_enable_add_meeting',
    defaultValue: false,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableMeetingFeatures: {
    flag: 'enable_meeting_features',
    defaultValue: true,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableFolderManagement: {
    flag: 'enable_folder_management',
    defaultValue: true,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableForms: {
    flag: 'enable_forms',
    defaultValue: true,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableContentSharing: {
    flag: 'enable_content_sharing',
    defaultValue: true,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableContentDownload: {
    flag: 'enable_content_download',
    defaultValue: true,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableHomeRecentlyViewedDocsWidget: {
    flag: 'enable_home_recently_viewed_docs_widget',
    defaultValue: true,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableHomeMostViewedDocsWidget: {
    flag: 'enable_home_most_viewed_docs_widget',
    defaultValue: true,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableHomeQuickFiltersWidget: {
    flag: 'enable_home_quick_filters_widget',
    defaultValue: true,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableHomeMyUpdatesWidget: {
    flag: 'enable_home_my_updates_widget',
    defaultValue: true,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableHomeRecommendedActionsWidget: {
    flag: 'enable_home_recommended_actions_widget',
    defaultValue: true,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableHomeRecentlyViewedFoldersWidget: {
    flag: 'enable_home_recently_viewed_folders_widget',
    defaultValue: true,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableHomeFavoriteFiles: {
    flag: 'enable_home_favorite_files',
    defaultValue: true,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableHomeFavoriteFolders: {
    flag: 'enable_home_favorite_folders',
    defaultValue: true,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableNotificationUpdates: {
    flag: 'enable_notification_updates',
    defaultValue: true,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  publisherShareSentDownloadReport: {
    flag: 'BEAC_3865_publisher_share_sent_download_report',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableCustomDecksCollaborationEdit: {
    flag: 'BEAC_2818_custom_deck_collaboration',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableMSLReports: {
    flag: 'BEAC_2388_msl_reports',
    defaultValue: false as boolean,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableZendeskKnowledgebase: {
    flag: 'BEAC_2519_zendesk_knowledgebase',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableZendeskChat: {
    flag: 'BEAC_3240_zendesk_chat',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableWebVideoDocuments: {
    flag: 'BEAC_3336_web_video_doc_types',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableHighlighter: {
    flag: 'BEAC_2820_highlighter',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableAllowAddFileToFolder: {
    flag: 'BEAC_1954_allow_add_file_to_folder',
    defaultValue: true,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableDemoMode: {
    flag: 'BEAC_3900_analytics_demo_mode',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_2777_immediate_publish_emails: {
    flag: 'BEAC_2777_immediate_publish_emails',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableZipDocuments: {
    flag: 'BEAC_3849_html_document_type',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableBulkAddToFolder: {
    flag: 'BEAC_1832_bulk_add_to_folder',
    defaultValue: true,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableBulkShareEmail: {
    flag: 'BEAC_2131_bulk_share_email',
    defaultValue: true as boolean,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableMeetingFollowUp: {
    flag: 'BEAC_4227_meeting_slide_follow_up',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableMeetingNotes: {
    flag: 'BEAC_4227_meeting_slide_notes',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableMeetingReactions: {
    flag: 'BEAC_4227_meeting_slide_reactions',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableMeetingEyeball: {
    flag: 'BEAC_4227_meeting_slide_eyeball',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  presentationVanityURL: {
    flag: 'BEAC_3569_presentation_vanity_url',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableHubs: {
    flag: 'BEAC_3259_beacon_hubs',
    defaultValue: false as boolean,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enablePublisherUpload: {
    flag: 'BEAC_5374_publisher_upload',
    defaultValue: true,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableCustomNotes: {
    flag: 'BEAC_5069_custom_notes',
    defaultValue: false as boolean,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableMyUploads: {
    flag: 'enable_viewer_file_upload',
    defaultValue: false,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enablePublisherReports: {
    flag: 'BEAC_1849_enable_publisher_reports',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  disablePublisherFolder: {
    flag: 'BEAC_2327_disable_publisher_folder',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableSharedFolders: {
    flag: 'enable_shared_folders',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableEmailNotification: {
    flag: 'BEAC_1535_msl_email_alerts',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableEditSharedFolder: {
    flag: 'BEAC_5647_edit_shared_folder',
    defaultValue: true,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableShareCustomDeck: {
    flag: 'BEAC_5351_enable_share_custom_decks',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enablePowerPointAddin: {
    flag: 'enable_powerpoint_addin',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  hideSlideTrackingReport: {
    flag: 'BEAC_5966_turn_off_slide_tracking_reporting',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  hideCopyTitleAndLink: {
    flag: 'BEAC_5702_disable_copy_title_and_link',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableSkipDatastoreOnReady: {
    flag: 'BEAC_5956_skip_datastore_onready',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  notifyOnlyOwnerOnExpiry: {
    flag: 'BEAC_5649_notify_only_owner_on_expiry',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableMultipleIFrames: {
    flag: 'BEAC_5977_enable_multiple_iframes_on_presentation',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_4251_slide_version_tracking: {
    flag: 'BEAC_4251_slide_version_tracking',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_6389_slide_text_insertion: {
    flag: 'BEAC_6389_slide_text_insertion',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_6655_custom_deck_usage: {
    flag: 'BEAC_6655_custom_deck_usage',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.USER_PERMISSION],
    ALLOWED_ROLES: STANDARD_ROLES,
  },
  BEAC_6763_first_match_slide_display: {
    flag: 'BEAC_6763_first_match_slide_display',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_5882_MSL_Download_PPTX_as_PDF: {
    flag: 'BEAC_5882_MSL_Download_PPTX_as_PDF',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableCRMSubmit: {
    flag: 'BEAC_7868_enable_crm_submit_final',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_8117_crm_ios_local_account_search: {
    flag: 'BEAC_8117_crm_ios_local_account_search',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_8226_external_oidc_vault_crm_sso: {
    flag: 'BEAC_8226_external_oidc_vault_crm_sso',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_7919_hide_save_to_beacon_button: {
    flag: 'BEAC_7919_hide_meeting_form_buttons',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableHybridSearch_V2_0: {
    flag: 'enableHybridSearch_V2_0',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableHybridSearch_V2_1: {
    flag: 'enableHybridSearch_V2_1',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableHybridSearch_V2_2: {
    flag: 'enableHybridSearch_V2_2',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  // Evaluated by the search Lambda from Tenant.featureFlags.
  BEAC_7679_semantic_search_max_score: {
    flag: 'BEAC_7679_semantic_search_max_score',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  // Evaluated by the search Lambda from Tenant.featureFlags.
  BEAC_7680_expanded_rerank_context: {
    flag: 'BEAC_7680_expanded_rerank_context',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_6085_Training_Event: { // note: this feature flag is used in alucio admin dashboard. Set the value from Launch Darkly instead a tenant dashboard
    flag: 'BEAC_6085_Training_Event',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_6984_email_template_type_selector: {
    flag: 'BEAC_6984_email_template_type_selector',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_6985_related_slides: {
    flag: 'BEAC_6985_related_slides',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_8431_allow_in_progress_related_slides: {
    flag: 'BEAC_8431_allow_in_progress_related_slides',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_6770_use_cosine_similarity_for_slide_matching: {
    flag: 'BEAC_6770_use_cosine_similarity_for_slide_matching',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_6094_hubs_templates: {
    flag: 'BEAC_6094_hubs_templates',
    defaultValue: false as boolean,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_7179_in_person_comments_and_sentiments: {
    flag: 'BEAC_7179_in_person_comments_and_sentiments',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_7165_avoid_duplicate_matches_in_slide_version_tracking: {
    flag: 'BEAC_7165_avoid_duplicate_matches_in_slide_version_tracking',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_7431_dynamic_zendesk_url: {
    flag: 'BEAC_7431_dynamic_zendesk_url',
    defaultValue: config.zendeskURL as string,
    CATEGORY: [FlagCategory.FEATURE],
  },
  beac_7469_publisher_run_integration: {
    flag: 'beac_7469-publisher_run_integration',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_7328_enable_document_groups_hubs: {
    flag: 'BEAC-7328_enable_document_groups_hubs',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_7570_compliance_checker: {
    flag: 'BEAC_7570_compliance_checker',
    defaultValue: false as boolean,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableOfflineMode: {
    flag: 'enable_offline_mode',
    defaultValue: true as boolean,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  enableCrmIntegration: {
    flag: 'enable_crm_integration',
    defaultValue: true as boolean,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.USER_PERMISSION],
  },
  BEAC_7921_crm_automatic_login: {
    flag: 'BEAC_7921_crm_automatic_login',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
    ALLOWED_ROLES: STANDARD_ROLES,
  },
  BEAC_7644_AI_search: {
    flag: 'BEAC_7644_AI_search',
    defaultValue: true as boolean,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_7936_ai_search_custom_decks: {
    flag: 'BEAC_7936_ai_search_custom_decks',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_8091_AI_search_view_only: {
    flag: 'BEAC_8091_AI_search_view_only',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_8089_publisher_in_progress_default_filters: {
    flag: 'BEAC_8089_publisher_in_progress_default_filters',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_7776_ai_search_current_flow: {
    flag: 'BEAC_7776_ai_search_current_flow',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_7776_document_search_agent_flow: {
    flag: 'BEAC_7776_document_search_agent_flow',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  enableAISearchFlowSelector: {
    flag: 'enable_ai_search_flow_selector',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_8540_ai_search_luna: {
    flag: 'BEAC_8540_ai_search_luna',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_7952_hide_create_hub: {
    flag: 'BEAC_7952_hide_create_hub',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_7666_enable_hidden_slides: {
    flag: 'BEAC_7666_enable_hidden_slides',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_8172_ai_document_labels: {
    flag: 'BEAC_8172_ai_document_labels',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_8317_enable_query_cleaning: {
    flag: 'BEAC_8317_enable_query_cleaning',
    defaultValue: false as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_7756_highlight_content_player_search: {
    flag: 'BEAC_7756_highlight_content_player_search',
    defaultValue: true as boolean,
    CATEGORY: [FlagCategory.FEATURE],
  },
  BEAC_8524_beacon_bulletin: {
    flag: 'BEAC_8524_beacon_bulletin',
    defaultValue: true as boolean,
    ALLOWED_ROLES: STANDARD_ROLES,
    CATEGORY: [FlagCategory.FEATURE],
  },
} as const satisfies Record<string, FeatureFlag>

export type FeatureFlagKeys = keyof typeof FeatureFlags

export type FeatureFlagConsts = { [K in FeatureFlagKeys]: typeof FeatureFlags[K]['defaultValue'] }
